import React from 'react';
import PropTypes from 'prop-types';

const SIZE_STYLES = {
  sm: {
    container: 'bg-gray-100 dark:bg-gray-700/50 p-0.5',
    button: 'px-2.5 py-1 text-xs',
    badge: 'text-[10px]',
  },
  lg: {
    container: 'bg-gray-100 dark:bg-gray-870 p-1',
    button: 'px-3.5 py-1.5 text-sm',
    badge: 'text-[11px]',
  },
};

/**
 * Generic segmented control, styled to match TrackSegmentedControl
 * (pill container, active option gets a raised white chip).
 *
 * Each option may carry an optional string `badge` or numeric `count`.
 * `badge` takes precedence (used for short metric labels like "RMSE").
 * `count` is rendered when it is greater than zero.
 * Both share the same pill styling (brand-tinted while active, muted otherwise).
 *
 * `fullWidth` stretches the container and its segments to fill the parent
 * (phone rows) and floors each segment at a 40px tap target; non-fullWidth
 * sizing is unchanged.
 */
export default function SegmentedControl({ options, value, onChange, ariaLabel, size = 'sm', fullWidth = false, containerClassName = '' }) {
  const sz = SIZE_STYLES[size] || SIZE_STYLES.sm;
  return (
    <div
      className={`${fullWidth ? 'flex w-full' : 'inline-flex'} rounded-lg ${sz.container} ${containerClassName}`}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const isActive = value === opt.value;
        const hasCount = typeof opt.count === 'number' && opt.count > 0;
        // badge: string label (e.g. metric name) takes precedence over numeric count
        const badgeContent = opt.badge != null ? opt.badge : (hasCount ? opt.count : null);
        const hasBadge = badgeContent != null;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={opt.tooltip || undefined}
            onClick={() => onChange(opt.value)}
            className={`
              group relative flex items-center gap-1.5 ${sz.button} rounded-md
              font-medium transition-all duration-150 ease-in-out
              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500
              ${fullWidth ? 'min-h-[40px] flex-1 justify-center' : ''}
              ${isActive
                ? 'bg-surface dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }
            `}
          >
            {opt.label}
            {hasBadge && (
              <span
                className={`
                  px-1.5 py-px rounded ${sz.badge} font-semibold transition-colors duration-150
                  ${isActive
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300'
                    : 'bg-gray-200/60 text-gray-400 dark:bg-gray-600/50 dark:text-gray-500'
                  }
                `}
              >
                {badgeContent}
              </span>
            )}
            {opt.tooltip && (
              <span
                className="opacity-0 w-max max-w-[16rem] absolute bottom-[calc(100%+0.5rem)] left-1/2 -translate-x-1/2
                  bg-gray-700 text-white text-xs font-normal rounded px-2 py-1
                  group-hover:opacity-100 transition pointer-events-none z-20"
              >
                {opt.tooltip}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      tooltip: PropTypes.string,
      count: PropTypes.number,
      badge: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg']),
  fullWidth: PropTypes.bool,
  containerClassName: PropTypes.string,
};
